import { defineMessages, useIntl } from 'react-intl';

import { ContactLink } from '@italia/components/ItaliaTheme/View';
import React from 'react';
import { Table } from 'design-react-kit/dist/design-react-kit';
import { UniversalLink } from '@plone/volto/components';
import { changeDateFormat } from '@package/helpers';

const messages = defineMessages({
  nome: {
    id: 'farmacia_table_nome',
    defaultMessage: 'Denominazione Farmacia',
  },
  nome_en: {
    id: 'farmacia_table_nome_en',
    defaultMessage: 'Pharmacy name',
  },
  comune: {
    id: 'farmacia_table_comune',
    defaultMessage: 'Comune',
  },
  comune_en: {
    id: 'farmacia_table_comune_en',
    defaultMessage: 'Municipality',
  },
  localita: {
    id: 'farmacia_table_localita',
    defaultMessage: 'Località',
  },
  localita_en: {
    id: 'farmacia_table_localita_en',
    defaultMessage: 'Locality',
  },
  indirizzo: {
    id: 'farmacia_table_indirizzo',
    defaultMessage: 'Indirizzo',
  },
  indirizzo_en: {
    id: 'farmacia_table_indirizzo_en',
    defaultMessage: 'Address',
  },
  recapiti: {
    id: 'farmacia_table_recapiti',
    defaultMessage: 'Recapiti',
  },
  recapiti_en: {
    id: 'farmacia_table_recapiti_en',
    defaultMessage: 'Contact numbers',
  },
  turni: {
    id: 'farmacia_table_turni',
    defaultMessage: 'Periodi di Turno',
  },
  turni_en: {
    id: 'farmacia_table_turni_en',
    defaultMessage: 'Shifts periods',
  },
  turni_type: {
    id: 'farmacia_table_turni_type',
    defaultMessage: 'Tipo turno',
  },
  turni_type_en: {
    id: 'farmacia_table_turni_type_en',
    defaultMessage: 'Shift type',
  },
  ferie: {
    id: 'farmacia_table_ferie',
    defaultMessage: 'Periodi di Ferie',
  },
  ferie_en: {
    id: 'farmacia_table_ferie_en',
    defaultMessage: 'Vacations',
  },
  period_from: {
    id: 'period_from',
    defaultMessage: 'Dal',
  },
  period_to: {
    id: 'period_to',
    defaultMessage: 'al',
  },
  telefono: {
    id: 'farmacia_table_telefono',
    defaultMessage: 'Telefono',
  },
  telefono_turno: {
    id: 'farmacia_table_telefono_turno',
    defaultMessage: 'Telefono turno',
  },
  noResult: {
    id: 'noResult',
    defaultMessage: 'Nessun risultato trovato',
  },
});

const ContactColumns = ({ isEditMode, item, searchType }) => {
  const intl = useIntl();

  return (
    <>
      {/* Comune */}
      <td className="comune">
        <div className="th d-lg-none">
          {intl.formatMessage(messages.comune)}
          <br />
          {intl.formatMessage(messages.comune_en)}
        </div>

        {item?.comune ? <p>{item.comune}</p> : <> - </>}
      </td>

      {/* Località */}
      <td className="localita">
        <div className="th d-lg-none">
          {intl.formatMessage(messages.localita)}
          <br />
          {intl.formatMessage(messages.localita_en)}
        </div>

        {item?.localita ? <p>{item.localita}</p> : <> - </>}
      </td>

      {/* Indirizzo */}
      <td className="indirizzo">
        <div className="th d-lg-none">
          {intl.formatMessage(messages.indirizzo)}
          <br />
          {intl.formatMessage(messages.indirizzo_en)}
        </div>

        {item?.street || item?.zip_code || item?.localita || item?.provincia ? (
          <div>
            {item?.street && item.street}
            {item?.street && item?.zip_code && <br />}
            {item?.zip_code && item.zip_code}
            {item?.zip_code && item?.localita && <> </>}
            {item?.localita && item.localita}
            {item?.provincia && <> ({item.provincia}) </>}
          </div>
        ) : (
          <> - </>
        )}
      </td>

      {/* Recapiti */}
      <td className="recapiti">
        <div className="th d-lg-none">
          {intl.formatMessage(messages.recapiti)}
        </div>

        {item?.telefono || item?.telefono_turno ? (
          <>
            {item.telefono && searchType === 'vacations' && (
              <p className="my-0">
                {intl.formatMessage(messages.telefono)}
                : <ContactLink tel={item.telefono} label={false} />
              </p>
            )}
            {item.telefono_turno && searchType !== 'vacations' && (
              <p className="my-0">
                {intl.formatMessage(messages.telefono_turno)}
                : <ContactLink tel={item.telefono_turno} label={false} />
              </p>
            )}
          </>
        ) : (
          <></>
        )}
      </td>
    </>
  );
};

const formatPeriodo = (dal, al) => {
  const parse = (value) => {
    const match = value
      .trim()
      .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);

    if (!match) return null;

    let [, dd, mm, yyyy, hh, min] = match;

    return {
      date: `${dd.padStart(2, '0')}/${mm.padStart(2, '0')}/${yyyy}`,
      time:
        hh !== undefined
          ? `${hh.padStart(2, '0')}:${min.padStart(2, '0')}`
          : null,
    };
  };

  const start = dal ? parse(dal) : '';
  const end = al ? parse(al) : '';

  const startPart = start
    ? start.time
      ? `Dalle ore ${start.time} del ${start.date}`
      : `Dal ${start.date}`
    : '';

  const endPart = end
    ? end.time
      ? `alle ore ${end.time} del ${end.date}`
      : `al ${end.date}`
    : '';

  return `${startPart} ${endPart}`;
};

const returnDates = (intl, period) => {
  // Dalle ore 08:30 del 15/12/2025 alle ore 20:30 del 22/12/2025
  // Dal 15/12/2023 al 10/1/2024
  return (
    <div className="period-wrap">
      <span className="pr-3">
        {formatPeriodo(period?.dal, period?.al)}
        <br />
      </span>
      <span className="period-type">
        {period?.tipo_turno ? period.tipo_turno : ''}
      </span>
    </div>
  );
};

const PeriodsStructure = ({ periods, dateFilter = null }) => {
  const intl = useIntl();

  return (
    <>
      {periods?.length > 0 ? (
        periods.map((pd) => {
          if (pd?.dal && pd?.al) {
            // anche se il turno ha un orario il controllo e su inizio/fine giornata
            const pd_dal = Date.parse(changeDateFormat(pd.dal, '00:00'));
            const pd_al = Date.parse(changeDateFormat(pd.al, '23:59'));

            if (dateFilter) {
              const dateSelected = Date.parse(dateFilter);
              if (dateSelected >= pd_dal && dateSelected <= pd_al) {
                return returnDates(intl, pd);
              }
            } else {
              return returnDates(intl, pd);
            }
          }
          return null;
        })
      ) : (
        <></>
      )}
    </>
  );
};

const Results = ({ items, isEditMode, resRef, searchType, dateFilter }) => {
  const intl = useIntl();

  return (
    <>
      {items?.length > 0 ? (
        <div
          id="farmacie-results"
          className="shadow"
          role="region"
          aria-live="polite"
          ref={resRef}
        >
          <Table>
            <thead>
              <tr className="d-none d-lg-table-row">
                <th>
                  {intl.formatMessage(messages.nome)} <br />
                  {intl.formatMessage(messages.nome_en)}
                </th>
                <th>
                  {intl.formatMessage(messages.comune)} <br />
                  {intl.formatMessage(messages.comune_en)}
                </th>
                <th>
                  {intl.formatMessage(messages.localita)}
                  <br />
                  {intl.formatMessage(messages.localita_en)}
                </th>
                <th>
                  {intl.formatMessage(messages.indirizzo)}
                  <br />
                  {intl.formatMessage(messages.indirizzo_en)}
                </th>

                {searchType === 'vacations' ? (
                  <>
                    <th>
                      {intl.formatMessage(messages.recapiti)}
                      <br />
                      {intl.formatMessage(messages.recapiti_en)}
                    </th>
                    <th>
                      {intl.formatMessage(messages.ferie)}
                      <br />
                      {intl.formatMessage(messages.ferie_en)}
                    </th>
                  </>
                ) : (
                  <>
                    <th>
                      {intl.formatMessage(messages.recapiti)}
                      <br />
                      {intl.formatMessage(messages.recapiti_en)}
                    </th>
                    <th>
                      {intl.formatMessage(messages.turni)}
                      <br />
                      {intl.formatMessage(messages.turni_en)}
                    </th>
                    <th className="period-type">
                      {intl.formatMessage(messages.turni_type)}
                      <br />
                      {intl.formatMessage(messages.turni_type_en)}
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i}>
                  <td className="nome">
                    {item['@id'] && (
                      <UniversalLink
                        item={!isEditMode ? item : null}
                        href={isEditMode ? '#' : null}
                      >
                        {item.title}
                      </UniversalLink>
                    )}
                  </td>
                  <ContactColumns
                    isEditMode={isEditMode}
                    item={item}
                    searchType={searchType}
                  />

                  {/* Periodi di turno */}
                  {searchType !== 'vacations' && (
                    <td className="turni" colSpan="2">
                      <div className="th d-lg-none">
                        {intl.formatMessage(messages.turni)}
                        <br />
                        {intl.formatMessage(messages.turni_en)}
                      </div>
                      <PeriodsStructure
                        periods={item.turni}
                        dateFilter={dateFilter}
                      />
                    </td>
                  )}

                  {/* Periodi di ferie */}
                  {searchType === 'vacations' && (
                    <td className="ferie">
                      <div className="th d-lg-none">
                        {intl.formatMessage(messages.ferie)}
                        <br />
                        {intl.formatMessage(messages.ferie_en)}
                      </div>
                      <PeriodsStructure periods={item.ferie} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="message px-4">
          {intl.formatMessage(messages.noResult)}
        </div>
      )}
    </>
  );
};

export default Results;
